import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CardSkeleton, FeedbackMessage } from "@/components/ui/feedback";
import { Input, Label, Textarea } from "@/components/ui/input";
import { DemoBanner } from "@/components/portal/DemoBanner";
import { APPLY_TYPE_LABEL, type Job } from "@/content/jobs";
import { DEPARTMENT_LABEL, canManageRecruiting, type Department } from "@/features/auth/types";
import { useAuth } from "@/features/auth/useAuth";
import { listAllJobs, saveJob, deleteJob, slugify } from "@/features/jobs/repo";

const DEPARTMENTS = Object.keys(DEPARTMENT_LABEL) as Department[];

const blankJob = (): Job => ({
  slug: "",
  title: "",
  applyType: "driver",
  employment: "Full-time",
  location: "",
  homeTime: "",
  pay: "",
  summary: "",
  requirements: [],
  highlights: [],
  department: "safety",
  published: false,
  order: 100,
});

export default function InternalJobs() {
  const { user } = useAuth();
  const canAccessJobs = canManageRecruiting(user);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Job | null>(null);
  /** True when the editor is for a brand-new posting (slug is editable). */
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteJob, setPendingDeleteJob] = useState<Job | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      setJobs(await listAllJobs());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load jobs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canAccessJobs) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [canAccessJobs]);

  function startAdd() {
    setError(null);
    setIsNew(true);
    setEditing(blankJob());
  }

  function startEdit(job: Job) {
    setError(null);
    setIsNew(false);
    setEditing({ ...job });
  }

  async function handleSave() {
    if (!editing) return;
    const title = editing.title.trim();
    if (!title) {
      setError("Title is required.");
      return;
    }
    const slug = isNew ? slugify(editing.slug || title) : editing.slug;
    if (!slug) {
      setError("Could not build a URL slug from that title.");
      return;
    }
    if (isNew && jobs.some((j) => j.slug === slug)) {
      setError(`A job with the slug "${slug}" already exists.`);
      return;
    }
    // Office postings must be tagged so their applications reach the right team.
    if (editing.applyType === "office" && !editing.department) {
      setError("Pick a reviewing department for this office posting.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveJob({ ...editing, slug, title });
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the job.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(job: Job) {
    setPendingDeleteJob(job);
  }

  async function confirmDeleteJob() {
    if (!pendingDeleteJob) return;
    try {
      await deleteJob(pendingDeleteJob.slug);
      setPendingDeleteJob(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the job.");
    }
  }

  async function togglePublished(job: Job) {
    try {
      await saveJob({ ...job, published: !job.published });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update the job.");
    }
  }

  const jobCounts = useMemo(
    () => ({
      published: jobs.filter((job) => job.published).length,
      drafts: jobs.filter((job) => !job.published).length,
      safety: jobs.filter((job) => job.department === "safety").length,
    }),
    [jobs],
  );

  if (!canAccessJobs) {
    return (
      <>
        <Seo title="Jobs" noindex />
        <h1 className="text-2xl font-bold">Job postings</h1>
        <p className="mt-4 rounded-lg border border-dashed border-brand-200 p-6 text-sm text-brand-500">
          Job posting management is available to admins and department managers.
          Regular dispatch staff do not need this recruiting workspace.
        </p>
      </>
    );
  }

  return (
    <>
      <Seo title="Jobs" noindex />
      <DemoBanner />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Job postings</h1>
          <p className="mt-1 text-sm text-brand-500">
            Add, edit, publish, or remove careers postings - no code needed. Published
            jobs appear on the public <span className="font-medium">Careers</span> page.
          </p>
        </div>
        <Button variant="accent" className="w-full sm:w-auto" onClick={startAdd}>
          <Plus className="mr-1 h-4 w-4" /> Add job
        </Button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <SummaryCard label="Published" value={jobCounts.published} />
        <SummaryCard label="Drafts" value={jobCounts.drafts} />
        <SummaryCard label="Safety review" value={jobCounts.safety} />
      </div>

      {error && (
        <FeedbackMessage tone="error" className="mt-4">
          {error}
        </FeedbackMessage>
      )}

      {/* List */}
      {loading ? (
        <div className="mt-6 grid gap-3">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : (
        <div className="mt-6 grid gap-3">
          {jobs.length === 0 && (
            <EmptyState
              title="No job postings yet"
              body='Click "Add job" to create the first public Careers posting. Drafts stay hidden until you publish them.'
            />
          )}
          {jobs.map((job) => (
            <div
              key={job.slug}
              className="rounded-xl border border-brand-100 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="break-words font-medium text-brand-900">{job.title}</p>
                    <Badge variant={job.published ? "success" : "default"}>
                      {job.published ? "Published" : "Draft"}
                    </Badge>
                    {job.department && (
                      <Badge variant="default">
                        {DEPARTMENT_LABEL[job.department]}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 break-words text-sm text-brand-500">
                    {[APPLY_TYPE_LABEL[job.applyType], job.employment, job.location, job.pay].filter(Boolean).join(" - ")}
                  </p>
                  <p className="mt-2 line-clamp-2 text-sm text-brand-600">{job.summary || "No summary yet."}</p>
                </div>
                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
                  <Button size="sm" variant="outline" onClick={() => togglePublished(job)}>
                    {job.published ? (
                      <>
                        <EyeOff className="mr-1 h-4 w-4" /> Unpublish
                      </>
                    ) : (
                      <>
                        <Eye className="mr-1 h-4 w-4" /> Publish
                      </>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(job)}>
                    <Pencil className="mr-1 h-4 w-4" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    title="Delete posting"
                    onClick={() => handleDelete(job)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {jobs.length > 0 && (
        <a
          href="/careers"
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-1 text-sm text-accent-600 hover:underline"
        >
          View the live careers page <ArrowUpRight className="h-4 w-4" />
        </a>
      )}

      {/* Editor */}
      {editing && (
        <JobEditor
          job={editing}
          isNew={isNew}
          saving={saving}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
      <ConfirmDialog
        open={!!pendingDeleteJob}
        title="Delete job posting?"
        body={`Delete "${pendingDeleteJob?.title ?? "this job"}"? This removes it from the staff list and public Careers page if published.`}
        confirmLabel="Delete posting"
        tone="danger"
        onConfirm={() => void confirmDeleteJob()}
        onCancel={() => setPendingDeleteJob(null)}
      />
    </>
  );
}

function JobEditor({
  job,
  isNew,
  saving,
  onChange,
  onCancel,
  onSave,
}: {
  job: Job;
  isNew: boolean;
  saving: boolean;
  onChange: (j: Job) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const set = <K extends keyof Job>(key: K, value: Job[K]) => onChange({ ...job, [key]: value });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-4 max-h-[92dvh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl sm:my-8 sm:p-6">
        <h2 className="text-xl font-bold">{isNew ? "Add job posting" : `Edit: ${job.title}`}</h2>

        <div className="mt-5 grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="title">Job title</Label>
              <Input
                id="title"
                value={job.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="OTR Tanker Driver"
              />
            </div>
            <div>
              <Label htmlFor="applyType">Applicant type</Label>
              <select
                id="applyType"
                className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm"
                value={job.applyType}
                onChange={(e) => {
                  const next = e.target.value as Job["applyType"];
                  // Driver/owner-operator applications always route to Safety.
                  onChange({
                    ...job,
                    applyType: next,
                    department: next === "office" ? job.department ?? "safety" : "safety",
                  });
                }}
              >
                <option value="driver">Company driver</option>
                <option value="carrier">Owner-operator</option>
                <option value="office">Office / Corporate</option>
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="department">Reviewing department</Label>
            <select
              id="department"
              className="flex h-10 w-full rounded-md border border-brand-200 bg-white px-3 text-sm disabled:opacity-60"
              value={job.department ?? ""}
              disabled={job.applyType !== "office"}
              onChange={(e) => set("department", (e.target.value || null) as Department | null)}
            >
              {job.applyType !== "office" && <option value="safety">Safety & Compliance</option>}
              {job.applyType === "office" && (
                <>
                  <option value="">Select department...</option>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {DEPARTMENT_LABEL[d]}
                    </option>
                  ))}
                </>
              )}
            </select>
            <p className="mt-1 text-xs text-brand-400">
              {job.applyType === "office"
                ? "Only this department's manager (and admins) will see these applicants."
                : "Driver & owner-operator applications always route to Safety."}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="employment">Employment</Label>
              <Input
                id="employment"
                value={job.employment}
                onChange={(e) => set("employment", e.target.value)}
                placeholder="Full-time - OTR"
              />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={job.location}
                onChange={(e) => set("location", e.target.value)}
                placeholder="Aurora, IL + surrounding lanes"
              />
            </div>
            <div>
              <Label htmlFor="homeTime">Home time</Label>
              <Input
                id="homeTime"
                value={job.homeTime}
                onChange={(e) => set("homeTime", e.target.value)}
                placeholder="Home most weekends"
              />
            </div>
            <div>
              <Label htmlFor="pay">Pay</Label>
              <Input
                id="pay"
                value={job.pay}
                onChange={(e) => set("pay", e.target.value)}
                placeholder="$75,000 - $90,000 / year"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="summary">Summary</Label>
            <Textarea
              id="summary"
              value={job.summary}
              onChange={(e) => set("summary", e.target.value)}
              placeholder="One or two sentences describing the role."
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="requirements">Requirements (one per line)</Label>
              <Textarea
                id="requirements"
                value={job.requirements.join("\n")}
                onChange={(e) => set("requirements", splitLines(e.target.value))}
                placeholder={"Valid Class A CDL\n2+ years experience"}
              />
            </div>
            <div>
              <Label htmlFor="highlights">What you get (one per line)</Label>
              <Textarea
                id="highlights"
                value={job.highlights.join("\n")}
                onChange={(e) => set("highlights", splitLines(e.target.value))}
                placeholder={"Late-model equipment\nSteady miles"}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="order">Sort order (lower shows first)</Label>
              <Input
                id="order"
                type="number"
                value={job.order}
                onChange={(e) => set("order", Number(e.target.value) || 0)}
              />
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm font-medium text-brand-800">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={job.published}
                onChange={(e) => set("published", e.target.checked)}
              />
              Published (visible on the public site)
            </label>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" className="w-full sm:w-auto" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="accent" className="w-full sm:w-auto" onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save job"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-brand-100 bg-white p-4 shadow-sm">
      <p className="text-2xl font-bold text-brand-900">{value}</p>
      <p className="text-sm text-brand-500">{label}</p>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed border-brand-200 bg-white p-6 text-center">
      <p className="font-medium text-brand-900">{title}</p>
      <p className="mt-1 text-sm text-brand-500">{body}</p>
    </div>
  );
}

function splitLines(value: string): string[] {
  return value
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}
